import { fileDownload } from 'utils/fileDownload';
import { logError } from 'utils/sentryUtils';

import { mmFetch } from './mmFetch';
import { mmFetchWithResponse } from './mmFetchWithResponse';

const shippingApi = {
    abandonBatches: async (packLineID) => {
        const url = `api/shipping/v1/batchelor/abandon`;
        return mmFetchWithResponse(url, {
            body: JSON.stringify({ packLineID }),
            method: 'PUT',
        });
    },
    abandonLineHaulBatch: async (packLineID, lineHaulID) => {
        const url = `api/shipping/v2/batchelor/abandon`;
        return mmFetchWithResponse(url, {
            body: JSON.stringify({ packLineID, lineHaulID }),
            method: 'PUT',
        });
    },
    cancelAddlLabel: async (shipmentID) => {
        const url = `api/shipping/v2/label/${shipmentID}/cancel`;
        return mmFetchWithResponse(url, {
            method: 'POST',
        });
    },
    claimBatches: async (data) => {
        const url = `api/shipping/v3/batchelor/claim`;
        return mmFetchWithResponse(url, {
            body: JSON.stringify(data),
            method: 'PUT',
        });
    },
    dbiDeepDive: async (orderNum, shipmentID) => {
        let url = `api/bringg/v1/deepDive?`;

        if (orderNum != null) {
            url += `orderNum=${orderNum}`;
        } else {
            url += `shipmentID=${shipmentID}`;
        }

        return mmFetchWithResponse(url);
    },
    getDBIRoutes: async (lineHaulID) => {
        const url = `api/shipping/v3/batchelor/dbiRoutes?lineHaulID=${lineHaulID}`;
        return mmFetchWithResponse(url);
    },
    getFailedShipments: async (facilityID) => {
        const url = `api/shipping/v1/shipment/failed?facilityID=${facilityID}`;
        return mmFetchWithResponse(url);
    },
    getShipmentAddress: async (id) => {
        const url = `api/shipping/v1/shipment/address/${id}`;
        return mmFetchWithResponse(url);
    },
    getShipmentLabelPDF: async (shipmentID) => {
        const url = `api/shipping/v1/shipment/${shipmentID}/pdf`;
        try {
            const response = await mmFetch(url);
            if (response.ok === true) {
                await fileDownload(response, `shipment_${shipmentID}.pdf`);
            }
            return !!response.ok;
        } catch (e) {
            logError(e);
            return false;
        }
    },
    downloadPickupLabel: async (shipmentID) => {
        const url = `api/shipping/v3/pickup/${shipmentID}`;
        try {
            const response = await mmFetch(url);
            if (response.ok === true) {
                await fileDownload(response, `shipment_${shipmentID}.pdf`);

                return { status: true };
            }
            return await response.json();
        } catch (e) {
            logError(e);
            return undefined;
        }
    },
    downloadPickupFacilityLabels: async (facilityID) => {
        const url = `api/shipping/v3/pickupFacility?facilityID=${facilityID}`;
        try {
            const response = await mmFetch(url);
            if (response.ok === true) {
                await fileDownload(response, `facilitypickup_${facilityID}.pdf`);
            }
            return !!response.ok;
        } catch (e) {
            logError(e);
            return false;
        }
    },
    downloadPickupCrossDockLabels: async (hub) => {
        const url = `api/shipping/v3/pickupCrossDock?hub=${hub}`;
        try {
            const response = await mmFetch(url);
            if (response.ok === true) {
                await fileDownload(response, `crossdockpickup_${hub}.pdf`);
            }
            return !!response.ok;
        } catch (e) {
            logError(e);
            return false;
        }
    },
    updateShipmentAddress: async (id, sendParams) => {
        const url = `api/shipping/v1/shipment/address/${id}`;
        return mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(sendParams),
        });
    },
    getLineHauls: async (facilityID, packGroupID = null) => {
        let url = `api/shipping/v2/batchelor/lineHauls?facility=${facilityID}`;
        if (packGroupID != null) {
            url += `&packGroupID=${packGroupID}`;
        }
        return mmFetchWithResponse(url);
    },
    getPackLinesWithClaims: async (facilityID) => {
        const url = `api/shipping/v1/batchelor/packLines?facility=${facilityID}`;
        return mmFetchWithResponse(url);
    },
    getPrintedNotPacked: async (facilityID, hours) => {
        const url = `api/shipping/v1/batchelor/printedNotPacked?facility=${facilityID}&hours=${hours}`;
        return mmFetchWithResponse(url);
    },
    getShipmentInfo: async (shipmentID) => {
        const url = `api/shipping/v1/shipment/${shipmentID}/info`;
        return mmFetchWithResponse(url);
    },
    getSpawnedShipments: async (facilityID) => {
        const url = `api/shipping/v2/label/new?facilityID=${facilityID}`;
        return mmFetchWithResponse(url);
    },
    printEntireWave: async (packLineID, multiLine = false) => {
        let url = `api/shipping/v3/batchelor/printAll?packLineID=${packLineID}`;
        if (multiLine) {
            url += '&multiLine=true';
        }

        const respObj = {
            status: true,
            data: {},
            msg: '',
        };

        try {
            const response = await mmFetch(url);

            if (response.ok === true) {
                if (multiLine && response.headers.get('Content-Type')?.includes('application/json')) {
                    return respObj;
                }

                await fileDownload(response, 'unknown_wave.pdf');
            } else {
                const decodedResp = await response.json();
                respObj.msg = decodedResp.msg;
                respObj.status = false;
            }
        } catch (e) {
            logError(e);
            respObj.msg = e.toString();
            respObj.status = false;
        }

        return respObj;
    },
    purchaseTestLabel: async (formData) => {
        const url = `api/shipping/v2/label/test`;
        try {
            const response = await mmFetch(url, {
                method: 'POST',
                body: JSON.stringify(formData),
            });
            if (response.ok === true) {
                await fileDownload(response, 'testlabel.pdf');
            }
            return response.ok;
        } catch (e) {
            logError(e);
            return false;
        }
    },
    purchaseShipment: async (shipmentID) => {
        const url = `api/shipping/v1/shipment/${shipmentID}/purchase`;
        return mmFetchWithResponse(url, {
            method: 'PUT',
        });
    },
    resyncBringg: async (orderNum) => {
        const url = `api/bringg/v1/resync`;
        return mmFetchWithResponse(url, {
            body: JSON.stringify({
                orderNum,
            }),
            method: 'POST',
        });
    },
    replaceShippingLabel: async (shipmentID, carrier, hub) => {
        const url = `api/shipping/v2/label/${shipmentID}/replace`;
        return mmFetchWithResponse(url, {
            body: JSON.stringify({
                carrier,
                hub,
            }),
            method: 'PUT',
        });
    },
    resetShipmentsToLabelPurchased: async (shipmentIDs) => {
        const url = `api/shipping/v1/reset`;
        return mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify({ shipmentIDs }),
        });
    },
    getPickupShipments: async (purchased, facilityID) => {
        let url = `api/shipping/v3/pickup?facilityID=${facilityID}`;
        if (purchased) {
            url += '&purchased=true';
        }
        return mmFetchWithResponse(url);
    },
    getUnprintedPickupDocks: async (facilityID) => {
        const url = `api/shipping/v3/pickupUnpackedDock?facilityID=${facilityID}`;
        return mmFetchWithResponse(url);
    },
    getVipShipments: async (purchased, facilityID) => {
        let url = `api/shipping/v1/vip?facilityID=${facilityID}`;
        if (purchased) {
            url += '&purchased=true';
        }
        return mmFetchWithResponse(url);
    },
    downloadLPN: async (shipmentID) => {
        const url = `api/shipping/v1/shipment/${shipmentID}/pdf`;
        try {
            const response = await mmFetch(url);
            if (response.ok === true) {
                await fileDownload(response, `vip-${shipmentID}.pdf`);
            }
            return !!response.ok;
        } catch (e) {
            logError(e);
            return false;
        }
    },
    markVipOrderPrinted: async (shipmentID) => {
        const url = `api/shipping/v1/vip/${shipmentID}/printed`;
        return mmFetchWithResponse(url, {
            method: 'PUT',
        });
    },
    getCarriersForOrder: async (orderNum) => {
        const url = `api/shipping/v1/shipment/${orderNum}/carriers`;
        return mmFetchWithResponse(url);
    },
    spawnNewShipment: async (shipmentID, cubeLevelID, facilityID) => {
        const url = `api/shipping/v2/label/${shipmentID}/new`;
        return mmFetchWithResponse(url, {
            body: JSON.stringify({
                cubeLevelID,
                facilityID,
            }),
            method: 'POST',
        });
    },
    getStuckShipments: async () => {
        const url = `api/shipping/v2/batchelor/stuck`;
        return mmFetchWithResponse(url);
    },
    resetStuckShipments: async () => {
        const url = `api/shipping/v2/batchelor/stuck`;
        return mmFetchWithResponse(url, {
            method: 'POST',
        });
    },
    uploadAddressesForCarrierShipLabels: async (data) => {
        const formData = new FormData();
        formData.append('addressFile', data.file);
        formData.append('facilityID', data.facilityID);
        formData.append('carrier', data.carrier);
        formData.append('shipDate', data.shipDate);

        const url = 'api/shipping/v1/importAddressesForCarrierLabels';
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
    },
    confirmAddressesForCarrierShipLabels: async (key) => {
        const url = `api/shipping/v1/confirmAddressesForCarrierLabels`;
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({ key }),
        });
    },
};

export { shippingApi };
